import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject, takeUntil } from 'rxjs';
import { CategoriesResponse } from "../../model/resource-model";
import { Store } from "@ngrx/store";
import { getCategoriesList } from "../../core/resources-hub.selectors";
import { FetchSubCategoriesListRequest, FetchSubCategoriesListSuccess } from "../../core/resources-hub.actions";

@Component({
  selector: 'app-add-categories',
  templateUrl: './add-categories.component.html',
  styleUrls: ['./add-categories.component.css']
})
export class AddCategoriesComponent implements OnInit, OnDestroy {
  categoriesDropDownOptions: CategoriesResponse[] = []


  isButtonDisabled: boolean = true;
  isFirstTimeChecked: boolean = false;

  categoriesForm: FormGroup = new FormGroup(
    {
      categories: new FormArray([])
    }
  );
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(private matDialogRef: MatDialogRef<AddCategoriesComponent>, @Inject(MAT_DIALOG_DATA) public data: any, private store: Store) {
  }


  ngOnInit(): void {
    this.getInitialData();
    this.disableDuplicateCategories();
    this.store.select(getCategoriesList).pipe(takeUntil(this.onDestroy))
      .subscribe(categories => {
        this.categoriesDropDownOptions = categories;
      });

    if (!this.isFirstTimeChecked) {
      this.setIntialData()
    }
  }

  getCategoryForm() {
    return (<FormArray>this.categoriesForm.get('categories')).controls
  }

  setIntialData() {
    const selectedCategoriesId: any[] = this.data.categories.map(selectedCategories => selectedCategories.name)
    const updatedData = this.categoriesDropDownOptions.map(categoriesOption => {
      if (selectedCategoriesId.includes(categoriesOption.key)) {
        return { ...categoriesOption, disabled: true }
      }
      else {
        return { ...categoriesOption, disabled: false, subCategories: null }
      }
    })
    this.isFirstTimeChecked = true;
    this.store.dispatch(FetchSubCategoriesListSuccess({ subCategoriesList: updatedData }))
  }


  disableDuplicateCategories(): void {
    this.categoriesForm.valueChanges.pipe(takeUntil(this.onDestroy))
      .subscribe(categories => {
        this.checkIsButtonDisabled(categories)
        const updatedList: any[] = this.categoriesDropDownOptions.map(options => ({
          ...options,
          disabled: false
        }))
        categories.categories.map((selectedCategory: any, index: number) => {
          const indexShouldBeDisabled = updatedList.findIndex(option => option.key === selectedCategory.name);
          if (indexShouldBeDisabled >= 0) {
            updatedList[indexShouldBeDisabled].disabled = true
          }
          if (selectedCategory.name === '' && selectedCategory.subCategories.length > 0) {
            (this.categoriesForm.get('categories') as FormArray).controls[index].patchValue({ subCategories: [] }, { emitEvent: false })
          }
        })
        this.categoriesDropDownOptions = [...updatedList]
        this.fetchSubCategories(categories)
      })
  }

  getInitialData(): void {
    if (this.data.categories.length > 0) {
      this.data.categories.map(category => {
        if (category.name !== '') {
          const formGroup = new FormGroup({
            name: new FormControl(category.name),
            id: new FormControl(category.id),
            subCategories: new FormControl([...category.subCategories])
          });
          (this.categoriesForm.get('categories') as FormArray).push(formGroup)
        }
      })
    } else {
      this.addCategory();
    }
  }

  addCategory(): void {
    const category: FormGroup = new FormGroup({
      name: new FormControl(''),
      id: new FormControl(''),
      subCategories: new FormControl([])
    });
    (this.categoriesForm.get('categories') as FormArray).push(category)
  }


  removeCategory(indexShouldBeRemoved: number) {
    (this.categoriesForm.get('categories') as FormArray).removeAt(indexShouldBeRemoved)
  }

  getSubCategoriesOptions(control: any) {
    if (control.value.name) {
      const selectedCategories = this.categoriesDropDownOptions.filter(category => category.key === control?.value.name)
      return selectedCategories[0]?.subCategories ? selectedCategories[0]?.subCategories : []
    }
    return []
  }

  getSubCategoriesLoading(control: any): boolean {
    if (control.value.name) {
      const selectedCategories = this.categoriesDropDownOptions.filter(category => category.key === control?.value.name)
      return !!selectedCategories[0]?.loading
    }
    return false
  }

  getSelectedCategoryName(categoryId: string) {
    if (categoryId !== '') {
      return this.categoriesDropDownOptions.filter(category => category.key === categoryId)[0].value
    }
    return ''
  }

  getSubCategoriesName(selectedCategory: string, selectedSubCategoriesId: string) {
    if (selectedCategory !== '' && selectedSubCategoriesId !== '') {
      const category = this.categoriesDropDownOptions.filter(category => category.key === selectedCategory)[0]
      return category.subCategories.filter(category => category.key === selectedSubCategoriesId)[0].value
    }
    return ''
  }

  conformButtonClicked(): void {
    const categoriesData = []
    this.categoriesForm.value.categories.map(category => {
      const subCategoriesData = []
      category.subCategories.map(selectedSubCategories => {
        subCategoriesData.push(this.getSubCategoriesName(category.name, selectedSubCategories))
      });
      categoriesData.push({
        name: this.getSelectedCategoryName(category.name),
        subCategories: subCategoriesData
      })
    })
    this.matDialogRef.close({
      data: { formValues: this.categoriesForm.value, categoriesDataMap: categoriesData },
      type: 'confirm'
    })
  }

  closeButtonClickHandler(): void {
    this.matDialogRef.close({
      data: [],
      type: 'close'
    })
  }

  checkIsButtonDisabled(categories: any): void {
    let isButtonStatusChanged: boolean = false;
    categories?.categories.map((selectedValues, index: number) => {
      if (selectedValues.name === '') {
        this.isButtonDisabled = true;
        isButtonStatusChanged = true
      } else if (index + 1 === categories?.categories.length && !isButtonStatusChanged) {
        this.isButtonDisabled = false;
      }
    })
  }

  fetchSubCategories(categoriesData): void {
    categoriesData.categories.map(categoriesSelectedData => {
      if (categoriesSelectedData.name && categoriesSelectedData.subCategories.length === 0) {
        const selectedCategoriesIndex: number = this.categoriesDropDownOptions.findIndex(categoriesData => categoriesData.key === categoriesSelectedData.name)
        if (selectedCategoriesIndex >= 0 && (!this.categoriesDropDownOptions[selectedCategoriesIndex].subCategories || this.categoriesDropDownOptions[selectedCategoriesIndex].subCategories.length === 0)) {
          const updatedCategoriesData = [...this.categoriesDropDownOptions]
          updatedCategoriesData[selectedCategoriesIndex] = { ...updatedCategoriesData[selectedCategoriesIndex], loading: true };
          this.store.dispatch(FetchSubCategoriesListRequest({
            payload: {
              categoryId: categoriesSelectedData.name,
              categoriesData: updatedCategoriesData
            }
          }))
        }
      }
    })

  }

  ngOnDestroy(): void {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
